import React, { useMemo } from 'react';
import { Box, Card, CardContent, InputAdornment, Stack } from '@mui/material';
import { observer } from 'mobx-react-lite';
import CommonInput from '../../../../component/form/common-input.component';
import TableComponent, { HeadCell } from '../../../../component/table/table.component';
import i18n from '../../../../i18n';
import { ErrorMessageBox } from '../../data-analysis.page';
import EfficiencyAnalysisState from '../state/efficiency-analysis-state';
import { ReactComponent as SearchIcon } from '../../../../static/images/svg/ic_round-search.svg';

/** 组件属性。 */
interface EfficiencyAnalysisTabPageProps {
    /** 出菜率分析状态。 */
    store: EfficiencyAnalysisState;
}

/**
 * 格式化分钟文本。
 * @param seconds - 秒数
 * @returns 分钟字符串
 */
function formatEfficiencyMinutes(seconds?: number | string) {
    /** 标准化后的秒数。 */
    const normalizedSeconds = Number(seconds ?? 0);
    return (normalizedSeconds / 60).toFixed(2);
}

/** 顶部下拉的统一样式。 */
const SELECT_INPUT_SX = {
    '& .base-input': {
        height: 56,
        width: '100%',
    },
    '& .MuiFormControl-root': {
        height: 56,
        width: '100%',
    },
    '& .MuiInputBase-root': {
        height: 56,
        width: '100%',
        pr: '16px',
    },
    '& .MuiOutlinedInput-root': {
        height: 56,
        minHeight: 56,
        width: '100%',
        borderRadius: '16px',
        boxShadow: '0px 0px 4px var(--shadow-50)',
        bgcolor: 'var(--fill-neutral-100)',
        '& .MuiSelect-select': {
            display: 'flex',
            alignItems: 'center',
            minHeight: '24px',
            py: '15px',
            pl: '16px',
            pr: '8px',
            boxSizing: 'border-box',
            color: 'var(--text-primary-300)',
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'transparent',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--stroke-primary-300)',
        },
        '&.Mui-disabled': {
            bgcolor: 'var(--fill-neutral-100)',
            opacity: 1,
            boxShadow: '0px 0px 4px var(--shadow-50)',
        },
        '& .MuiSelect-select.Mui-disabled': {
            WebkitTextFillColor: 'var(--text-primary-300)',
            opacity: 0.56,
        },
    },
};

/**
 * 出菜率分析页签。
 */
const EfficiencyAnalysisTabPage = observer(({ store }: EfficiencyAnalysisTabPageProps) => {
    /**
     * 读取指定列的前端排序箭头状态。
     * @param key - 表头列 id
     * @returns 0 未排序 / 1 降序 / 2 升序
     */
    const getSortStatus = (key: string) => {
        /** 当前排序配置。 */
        const sort = store.sortKeyValue;
        if (key === sort?.key) {
            return sort.value === 'ASC' ? 2 : 1;
        }
        return 0;
    };

    /**
     * 表头点击切换排序（无排序 → 降序 → 升序 → 无排序）。
     * @param headCell - 被点击的表头
     */
    const handleSort = (headCell: HeadCell) => {
        /** 下一档排序方向。 */
        let sortStatus: 'ASC' | 'DESC' | '' = '';
        switch (headCell.sortStatus) {
            case 0:
                sortStatus = 'DESC';
                break;
            case 1:
                sortStatus = 'ASC';
                break;
            case 2:
                sortStatus = '';
                break;
            default:
                sortStatus = '';
                break;
        }
        store.setSortValue({
            value: sortStatus ? sortStatus : undefined,
            key: sortStatus ? headCell.id : undefined,
        });
    };

    /** 表头配置。 */
    const headCellList = useMemo<HeadCell[]>(() => [
        {
            id: 'menu_item_name',
            label: i18n.t('business_analysis_page_dishes_name_one'),
            minWidth: '320px',
            sortStatus: getSortStatus('menu_item_name'),
            renderFunction: (row) => row?.menu_item_name ?? '--',
        },
        {
            id: 'item_name2',
            label: i18n.t('business_analysis_page_dishes_name_two'),
            minWidth: '320px',
            sortStatus: getSortStatus('item_name2'),
            renderFunction: (row) => row?.item_name2 ?? '--',
        },
        {
            id: 'quantity',
            label: i18n.t('business_analysis_page_text_sum_quantity'),
            minWidth: '240px',
            sortStatus: getSortStatus('quantity'),
            renderFunction: (row) => row?.quantity ?? 0,
        },
        {
            id: 'average_time',
            label: i18n.t('business_analysis_page_average_time_minute'),
            minWidth: '240px',
            sortStatus: getSortStatus('average_time'),
            renderFunction: (row) => formatEfficiencyMinutes(row?.average_time),
        },
    ], [store.sortKeyValue]);
    /** 搜索框输入样式。 */
    const searchInputProps = useMemo(() => ({
        endAdornment: (
            <InputAdornment position="end" sx={{ mr: 0 }}>
                <SearchIcon style={{ width: 24, height: 24, color: 'var(--text-primary-300)' }} />
            </InputAdornment>
        ),
    }), []);

    if (store.errorMessage) {
        return <ErrorMessageBox errorMessage={store.errorMessage} height={360} />;
    }

    return (
        <Stack spacing={3} sx={{ minHeight: 'calc(100vh - 260px)' }}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between', flexWrap: 'nowrap', alignItems: 'center', overflowX: 'auto', overflowY: 'visible', px: '4px', py: '4px' }}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'nowrap', flexShrink: 0 }}>
                    <Box sx={{ width: 248, height: 56, flexShrink: 0 }}>
                        <CommonInput
                            inputType="SELECT"
                            select
                            size="small"
                            items={store.mainGroups}
                            value={store.mainGroupId}
                            onChange={(event) => store.setMainGroupId(event.target.value as string)}
                            sx={SELECT_INPUT_SX}
                            fullWidth
                        />
                    </Box>
                    <Box sx={{ width: 248, height: 56, flexShrink: 0 }}>
                        <CommonInput
                            inputType="SELECT"
                            select
                            size="small"
                            items={store.secondMenuOptions}
                            value={store.secondMenuId}
                            onChange={(event) => store.setSecondMenuId(event.target.value as string)}
                            sx={SELECT_INPUT_SX}
                            fullWidth
                        />
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'nowrap', alignItems: 'center', flexShrink: 0 }}>
                    <Box sx={{ width: 160, flexShrink: 0 }}>
                        <CommonInput
                            inputType="INPUT"
                            size="small"
                            placeholder={i18n.t('general_text_search')}
                            value={store.searchKeyword}
                            onChange={(event) => store.setSearchKeyword(event.target.value)}
                            InputProps={searchInputProps}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    height: 56,
                                    minHeight: 56,
                                    borderRadius: '16px',
                                    boxShadow: '0px 0px 4px var(--shadow-50)',
                                    bgcolor: 'var(--fill-neutral-100)',
                                    boxSizing: 'border-box',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'transparent',
                                    },
                                    '& input': {
                                        py: '15px',
                                        px: '16px',
                                        boxSizing: 'border-box',
                                        color: 'var(--text-primary-300)',
                                    },
                                    '& input::placeholder': {
                                        color: 'var(--text-primary-300)',
                                        opacity: 1,
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'var(--stroke-primary-300)',
                                    },
                                },
                            }}
                        />
                    </Box>
                </Box>
            </Box>
            <Card
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0,
                    borderRadius: '16px',
                    boxShadow: '0px 0px 4px var(--shadow-25)',
                }}
            >
                <CardContent
                    sx={{
                        display: 'flex',
                        flex: 1,
                        minHeight: 0,
                        p: 0,
                        '&:last-child': { pb: 0 },
                    }}
                >
                    <Box sx={{ flex: 1, minHeight: 0, p: 3 }}>
                        <TableComponent
                            isPagination
                            allCount={store.filteredRows.length}
                            headCellList={headCellList}
                            list={store.filteredRows}
                            showChecked={0}
                            isTableLoading={store.loading}
                            handleSort={handleSort}
                        />
                    </Box>
                </CardContent>
            </Card>
        </Stack>
    );
});

export default EfficiencyAnalysisTabPage;
